import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../app/store';
import { fetchPurchaseOrderDetail, clearCurrentPo, fetchPoFiltersData, updatePurchaseOrder, createPurchaseOrder, sendSupplier, saveQuotation, splitPurchaseOrder, submitPurchaseOrder } from './poSlice';
import { toast } from 'react-hot-toast';
import {
  fetchCategoriesApi, fetchSubcategoriesApi, deletePoDocumentApi, pullFromAtrApi,
  importQuotationLineItemsApi, deleteQuotationLineItemsApi, downloadExcelTemplateApi,
} from '../../services/api';
import type { AuthUser } from '../auth/authSlice';

const PurchaseOrderEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  
  const { current: po, detailLoading, error, filtersData } = useSelector((state: RootState) => state.purchaseOrders);
  const authUser = useSelector((state: RootState) => state.auth.user as AuthUser | null);

  const isCreateMode = !id || id === 'create';
  const poId = isCreateMode ? undefined : Number(id);
  
  const [activeTab, setActiveTab] = useState<'basic-info' | 'supplier' | 'items' | 'approval'>('basic-info');
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [subcategories, setSubcategories] = useState<{ id: number; name: string; repair_code?: string; purchase_code?: string }[]>([]);
  const [manualItems, setManualItems] = useState<{ asset: string; description: string }[]>([{ asset: '', description: '' }]);
  const [excelImportCollapsed, setExcelImportCollapsed] = useState(false);
  const [selectedAreaCoach, setSelectedAreaCoach] = useState<number | null>(null);
  const [selectedRegional, setSelectedRegional] = useState<number | null>(null);
  const [isStoreDropdownOpen, setIsStoreDropdownOpen] = useState(false);
  const [storeDropdownRef, setStoreDropdownRef] = useState<HTMLDivElement | null>(null);
  const [storeSearchQuery, setStoreSearchQuery] = useState('');
  const [requireSecondApprover, setRequireSecondApprover] = useState(false);

  // Admin override properties (mirrors Livewire $adminOverride / $adminOverrideReason)
  const [adminOverride, setAdminOverride] = useState(false);
  const [adminOverrideReason, setAdminOverrideReason] = useState('');

  const [quoteCollapsed, setQuoteCollapsed] = useState(false);
  const [showTbaModal, setShowTbaModal] = useState(false);
  const [tbaApprover, setTbaApprover] = useState<number | null>(null);
  const [quotationSaved, setQuotationSaved] = useState(true);
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [excelImporting, setExcelImporting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const ignoreStaleFetch = useRef(false);

  const storeSelectAllExclusions = ['ADMIN', 'IT', 'Maintenance', 'OPERATIONS', 'TEST', 'Training'];

  const [formData, setFormData] = useState({
    date: '',
    store_ids: [] as number[],
    requested_by_id: null as number | null,
    required_jobcard: 0 as number,
    quotation_date: '',
    quotation_number: '',
    repair_cost: '',
    vat: 0 as number,
    internal: 0,
    supplier_id: null as number | null,
    technician_id: null as number | null,
    department_id: null as number | null,
    category_id: null as number | null,
    subcategory_id: null as number | null,
    is_repair: null as number | null,
    is_order: 0 as number,
    approver_id: null as number | null,
    second_approver_id: null as number | null,
    fulfillment_id: null as number | null,
    comment: '',
  });

  useEffect(() => {
    dispatch(fetchPoFiltersData());
    if (isCreateMode) {
      ignoreStaleFetch.current = true;
      dispatch(clearCurrentPo());
      setFormData({
        date: new Date().toISOString().split('T')[0],
        store_ids: [],
        requested_by_id: authUser?.id ?? null,
        required_jobcard: 0,
        quotation_date: '',
        quotation_number: '',
        repair_cost: '',
        vat: 0,
        internal: 0,
        supplier_id: null,
        technician_id: null,
        department_id: null,
        category_id: null,
        subcategory_id: null,
        is_repair: null,
        is_order: 0,
        approver_id: null,
        second_approver_id: null,
        fulfillment_id: null,
        comment: '',
      });
      return;
    }
    ignoreStaleFetch.current = false;
    if (!poId) return;
    dispatch(fetchPurchaseOrderDetail(poId));
    return () => {
      dispatch(clearCurrentPo());
    };
  }, [dispatch, poId, isCreateMode, authUser?.id]);

  // Guard: if a stale fetchPurchaseOrderDetail resolves after we switched to create mode,
  // clear the stale data from Redux state.
  useEffect(() => {
    if (isCreateMode && po) {
      dispatch(clearCurrentPo());
    }
  }, [isCreateMode, po, dispatch]);

  // Fetch categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await fetchCategoriesApi();
        setCategories(data);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Close store dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (storeDropdownRef && !storeDropdownRef.contains(e.target as Node)) {
        setIsStoreDropdownOpen(false);
        setStoreSearchQuery('');
      }
    };
    if (isStoreDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isStoreDropdownOpen, storeDropdownRef]);

  // Fetch subcategories when category changes
  useEffect(() => {
    if (formData.category_id) {
      fetchSubcategoriesApi(formData.category_id)
        .then((data) => setSubcategories(data))
        .catch((error) => {
          console.error('Failed to fetch subcategories:', error);
          setSubcategories([]);
        });
    } else {
      setSubcategories([]);
    }
  }, [formData.category_id]);

  useEffect(() => {
    if (po && !isCreateMode) {
      setFormData({
        date: po.date || '',
        store_ids: po.stores?.map((s: any) => s.id) || [],
        requested_by_id: typeof po.requested_by === 'number' ? po.requested_by : (po.requested_by as any)?.id || null,
        required_jobcard: po.required_jobcard ? 1 : 0,
        quotation_date: (po.quotation as any)?.date || '',
        quotation_number: (po.quotation as any)?.quotation_number || '',
        repair_cost: (po.quotation as any)?.amount?.toString() || '',
        vat: (po.quotation as any)?.vat ? 1 : 0,
        internal: po.internal || 0,
        supplier_id: po.supplier?.id || null,
        technician_id: po.technician?.id || null,
        department_id: po.department?.id || null,
        category_id: po.category?.id || null,
        subcategory_id: po.subCategory?.id || null,
        is_repair: null,
        is_order: typeof po.is_order === 'boolean' ? (po.is_order ? 1 : 0) : (po.is_order || 0),
        approver_id: po.approvers?.[0]?.user_id || null,
        second_approver_id: null,
        fulfillment_id: po.fulfillment?.id || null,
        comment: '',
      });
    }
  }, [po]);

  // Track quotation field changes to show unsaved warning
  useEffect(() => {
    if (!isCreateMode && poId) {
      setQuotationSaved(false);
    }
  }, [formData.quotation_date, formData.quotation_number, formData.repair_cost, formData.vat, isCreateMode, poId]);

  const handleTabChange = (tab: typeof activeTab) => {
    setActiveTab(tab);
  };

  const handleNextTab = () => {
    const tabs: typeof activeTab[] = ['basic-info', 'supplier', 'items', 'approval'];
    const currentIndex = tabs.indexOf(activeTab);
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1]);
    }
  };

  const handlePreviousTab = () => {
    const tabs: typeof activeTab[] = ['basic-info', 'supplier', 'items', 'approval'];
    const currentIndex = tabs.indexOf(activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1]);
    }
  };

  const handleSave = async () => {
    if (isCreateMode) {
      try {
        const result = await dispatch(createPurchaseOrder({
          date: formData.date,
          store_ids: formData.store_ids,
          requested_by_id: formData.requested_by_id,
          required_jobcard: formData.required_jobcard,
          quotation_date: formData.quotation_date,
          quotation_number: formData.quotation_number,
          repair_cost: formData.repair_cost,
          vat: formData.vat,
          internal: formData.internal,
          supplier_id: formData.supplier_id,
          technician_id: formData.technician_id,
          department_id: formData.department_id,
          category_id: formData.category_id,
          subcategory_id: formData.subcategory_id,
          is_repair: formData.is_repair ?? 0,
          is_order: formData.is_order,
          approver_id: formData.approver_id,
          second_approver_id: formData.second_approver_id,
          fulfillment_id: formData.fulfillment_id,
          comment: formData.comment,
        })).unwrap();
        toast.success('PO created successfully');
        if (result.data?.id) {
          navigate(`/purchase-orders/${result.data.id}`);
        }
      } catch (error: any) {
        toast.error(error || 'Failed to create PO');
      }
      return;
    }

    if (!poId) return;

    try {
      await dispatch(updatePurchaseOrder({
        id: poId,
        data: {
          date: formData.date,
          store_ids: formData.store_ids,
          requested_by_id: formData.requested_by_id,
          required_jobcard: formData.required_jobcard,
          quotation_date: formData.quotation_date,
          quotation_number: formData.quotation_number,
          repair_cost: formData.repair_cost,
          vat: formData.vat,
          internal: formData.internal,
          supplier_id: formData.supplier_id,
          technician_id: formData.technician_id,
          department_id: formData.department_id,
          category_id: formData.category_id,
          subcategory_id: formData.subcategory_id,
          is_repair: formData.is_repair ?? 0,
          is_order: formData.is_order,
          approver_id: formData.approver_id,
          second_approver_id: formData.second_approver_id,
          fulfillment_id: formData.fulfillment_id,
          comment: formData.comment,
        },
      })).unwrap();
      toast.success('PO updated successfully');
    } catch (error: any) {
      toast.error(error || 'Failed to update PO');
    }
  };

  const handleSubmit = async () => {
    if (!poId) return;
    setActionLoading(true);
    try {
      await dispatch(submitPurchaseOrder(poId)).unwrap();
      toast.success('PO submitted for approval');
      navigate(`/purchase-orders/${poId}`);
    } catch (err: any) {
      toast.error(err || 'Failed to submit PO');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTbaSubmit = async () => {
    if (!poId || !tbaApprover) return;
    setActionLoading(true);
    try {
      await dispatch(sendSupplier({ id: poId, tbaApprover })).unwrap();
      toast.success('Order sent for TBA assessment');
      setShowTbaModal(false);
      setTbaApprover(null);
      navigate(`/purchase-orders/${poId}`);
    } catch (err: any) {
      toast.error(err || 'Failed to submit for TBA');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveQuotation = async () => {
    if (!poId) return;
    setActionLoading(true);
    try {
      await dispatch(saveQuotation({
        id: poId,
        data: {
          quotation_date: formData.quotation_date,
          quotation_number: formData.quotation_number,
          repair_cost: formData.repair_cost ? Number(formData.repair_cost) : undefined,
          vat: formData.vat,
        },
      })).unwrap();
      toast.success('Quotation details saved');
      setQuotationSaved(true);
      dispatch(fetchPurchaseOrderDetail(poId));
    } catch (err: any) {
      toast.error(err || 'Failed to save quotation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSplitOrder = async () => {
    if (!poId) return;
    if (!window.confirm('Split this order into separate orders per store?')) return;
    setActionLoading(true);
    try {
      await dispatch(splitPurchaseOrder(poId)).unwrap();
      toast.success('Order split successfully');
      navigate('/purchase-orders');
    } catch (err: any) {
      toast.error(err || 'Failed to split order');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExcelImport = async () => {
    if (!poId || !excelFile) return;
    setExcelImporting(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', excelFile);
      const result = await importQuotationLineItemsApi(poId, formDataObj);
      toast.success(result.message || 'Items imported successfully');
      setExcelFile(null);
      dispatch(fetchPurchaseOrderDetail(poId));
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to import Excel');
    } finally {
      setExcelImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadExcelTemplateApi();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'quotation_line_items_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      toast.error('Failed to download template');
    }
  };

  const handleClearAllItems = async () => {
    if (!poId) return;
    if (!window.confirm('Remove all line items? This cannot be undone.')) return;
    try {
      await deleteQuotationLineItemsApi(poId);
      toast.success('All items cleared');
      dispatch(fetchPurchaseOrderDetail(poId));
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to clear items');
    }
  };

  const isQuoteLocked = !isCreateMode && !!po?.order_number && po?.status_id === 2;

  const handleRemoveQuote = async () => {
    if (!po?.quotation?.id || !id) return;
    if (isQuoteLocked) {
      toast.error('Cannot remove quotation from an approved order with an order number.');
      return;
    }
    if (!window.confirm('Are you sure you want to remove this quotation?')) {
      return;
    }

    try {
      await deletePoDocumentApi(Number(id), 'quotation', po.quotation.id);
      toast.success('Quotation removed successfully');
      dispatch(fetchPurchaseOrderDetail(Number(id)));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to remove quotation');
    }
  };

  const handlePullFromAtr = async () => {
    if (!id) return;
    if (!window.confirm('This will replace all current items with the latest data from the linked ATR/Requisition. Continue?')) {
      return;
    }

    try {
      await pullFromAtrApi(Number(id));
      toast.success('Successfully pulled items from ATR/Requisition');
      dispatch(fetchPurchaseOrderDetail(Number(id)));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to pull from ATR');
    }
  };

  const addManualItem = () => {
    setManualItems([...manualItems, { asset: '', description: '' }]);
  };

  const removeManualItem = (index: number) => {
    setManualItems(manualItems.filter((_, i) => i !== index));
  };

  const updateManualItem = (index: number, field: 'asset' | 'description', value: string) => {
    const updated = [...manualItems];
    updated[index][field] = value;
    setManualItems(updated);
  };

  const handleStoreSelection = (storeId: number) => {
    if (formData.store_ids.includes(storeId)) {
      setFormData({ ...formData, store_ids: formData.store_ids.filter(id => id !== storeId) });
    } else {
      setFormData({ ...formData, store_ids: [...formData.store_ids, storeId] });
    }
  };

  const selectAllStores = () => {
    if (filtersData?.stores) {
      const eligibleStores = filtersData.stores.filter((store: any) =>
        !storeSelectAllExclusions.includes(store.name)
      );
      setFormData({ ...formData, store_ids: eligibleStores.map(s => s.id) });
    }
  };

  const clearAllStores = () => {
    setFormData({ ...formData, store_ids: [] });
  };

  const getStoresByAreaCoach = (coachId: number | null): number[] => {
    if (!coachId || !filtersData?.stores) return [];
    return filtersData.stores
      .filter((store: any) => store.area_id === coachId)
      .map((store: any) => store.id);
  };

  const getStoresByRegional = (regionalId: number | null): number[] => {
    if (!regionalId || !filtersData?.stores) return [];
    return filtersData.stores
      .filter((store: any) => store.regional_id === regionalId)
      .map((store: any) => store.id);
  };

  const addAreaCoachStores = () => {
    const coachStores = getStoresByAreaCoach(selectedAreaCoach);
    const newStoreIds = [...formData.store_ids, ...coachStores].filter((id, index, arr) => arr.indexOf(id) === index);
    setFormData({ ...formData, store_ids: newStoreIds });
  };

  const removeAreaCoachStores = () => {
    const coachStores = getStoresByAreaCoach(selectedAreaCoach);
    const newStoreIds = formData.store_ids.filter(id => !coachStores.includes(id));
    setFormData({ ...formData, store_ids: newStoreIds });
  };

  const addRegionalStores = () => {
    const regionalStores = getStoresByRegional(selectedRegional);
    const newStoreIds = [...formData.store_ids, ...regionalStores].filter((id, index, arr) => arr.indexOf(id) === index);
    setFormData({ ...formData, store_ids: newStoreIds });
  };

  const removeRegionalStores = () => {
    const regionalStores = getStoresByRegional(selectedRegional);
    const newStoreIds = formData.store_ids.filter(id => !regionalStores.includes(id));
    setFormData({ ...formData, store_ids: newStoreIds });
  };

  if (detailLoading && !isCreateMode) {
    return <div className="text-center p-10 dark:text-gray-300">Loading purchase order for editing...</div>;
  }

  if (error && !isCreateMode) {
    return (
      <div className="text-center p-10 dark:text-gray-300">
        <div className="text-red-500 mb-4">Error loading purchase order</div>
        <div>{error}</div>
        <button onClick={() => navigate(-1)} className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
          Go Back
        </button>
      </div>
    );
  }

  if (!po && !isCreateMode) {
    return (
      <div className="text-center p-10 dark:text-gray-300">
        Purchase order not found.
        <button onClick={() => navigate(-1)} className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-4 lg:p-4 max-w-8xl">
      <button
        onClick={() => navigate(isCreateMode ? '/purchase-orders' : `/purchase-orders/${po?.id}`)}
        className="mb-4 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 transition-colors"
      >
        &larr; {isCreateMode ? 'Back to Purchase Orders' : 'Back to Details'}
      </button>

      <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg overflow-hidden">
        {/* Header */}
        <div className="p-4 md:p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
            {isCreateMode ? 'Create New Purchase Order' : `Edit Purchase Order #${po?.order_number || po?.id}`}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {isCreateMode ? 'Draft — not yet submitted' : (po?.status_id === 1 ? 'Draft' : po?.status_id === 2 ? 'Approved' : po?.status_id === 3 ? 'Rejected' : 'Cancelled')}
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <nav className="flex space-x-0" aria-label="Tabs">
            <button
              onClick={() => handleTabChange('basic-info')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'basic-info'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Basic Info
              </span>
            </button>
            <button
              onClick={() => handleTabChange('supplier')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'supplier'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                Supplier/Technician
              </span>
            </button>
            <button
              onClick={() => handleTabChange('items')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'items'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Items
              </span>
            </button>
            <button
              onClick={() => handleTabChange('approval')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'approval'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Save & Submit
              </span>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-4 md:p-6">
          {activeTab === 'basic-info' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Request Info */}
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600">
                  <h4 className="font-semibold text-gray-800 dark:text-white mb-4">Request Info</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Stores <span className="text-red-500">*</span>
                      </label>
                      <div className="mb-2 flex flex-wrap gap-2">
                        <select
                          value={selectedAreaCoach || ''}
                          onChange={(e) => setSelectedAreaCoach(e.target.value ? Number(e.target.value) : null)}
                          className="px-3 py-1.5 rounded-lg text-sm border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
                        >
                          <option value="">-- Area Coach's Stores --</option>
                          {filtersData?.areaCoaches?.map((coach: any) => (
                            <option key={coach.id} value={coach.id}>{coach.name}</option>
                          ))}
                        </select>
                        <div className="flex gap-1 items-center">
                          <button
                            type="button"
                            onClick={addAreaCoachStores}
                            disabled={!selectedAreaCoach}
                            className="flex items-center justify-center w-7 h-7 text-xs font-bold bg-green-600 hover:bg-green-700 text-white rounded-md shadow-sm disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:text-gray-500 dark:disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                            title="Add coach's stores to selection"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={removeAreaCoachStores}
                            disabled={!selectedAreaCoach}
                            className="flex items-center justify-center w-7 h-7 text-xs font-bold bg-red-600 hover:bg-red-700 text-white rounded-md shadow-sm disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:text-gray-500 dark:disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                            title="Remove coach's stores from selection"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 12H4" />
                            </svg>
                          </button>
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                            Add/Remove coach's stores
                          </span>
                        </div>
                        <select
                          value={selectedRegional || ''}
                          onChange={(e) => setSelectedRegional(e.target.value ? Number(e.target.value) : null)}
                          className="px-3 py-1.5 rounded-lg text-sm border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
                        >
                          <option value="">-- Regional's Stores --</option>
                          {filtersData?.regionals?.map((regional: any) => (
                            <option key={regional.id} value={regional.id}>{regional.name}</option>
                          ))}
                        </select>
                        <div className="flex gap-1 items-center">
                          <button
                            type="button"
                            onClick={addRegionalStores}
                            disabled={!selectedRegional}
                            className="flex items-center justify-center w-7 h-7 text-xs font-bold bg-green-600 hover:bg-green-700 text-white rounded-md shadow-sm disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:text-gray-500 dark:disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                            title="Add regional's stores to selection"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={removeRegionalStores}
                            disabled={!selectedRegional}
                            className="flex items-center justify-center w-7 h-7 text-xs font-bold bg-red-600 hover:bg-red-700 text-white rounded-md shadow-sm disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:text-gray-500 dark:disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                            title="Remove regional's stores from selection"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 12H4" />
                            </svg>
                          </button>
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                            Add/Remove regional's stores
                          </span>
                        </div>
                      </div>
                      <div className="mb-2 flex gap-2">
                        <button
                          type="button"
                          onClick={selectAllStores}
                          className="px-3 py-1.5 bg-gray-800 text-white rounded text-sm hover:bg-gray-700 transition-colors"
                        >
                          Select All Stores
                        </button>
                        <button
                          type="button"
                          onClick={clearAllStores}
                          className="px-3 py-1.5 bg-gray-600 text-white rounded text-sm hover:bg-gray-500 transition-colors"
                        >
                          Clear All
                        </button>
                      </div>

                      {/* Searchable multi-select dropdown */}
                      <div ref={setStoreDropdownRef} className="relative">
                        {/* Selected stores as chips */}
                        {formData.store_ids.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {formData.store_ids.map((storeId) => {
                              const store = filtersData?.stores?.find((s: any) => s.id === storeId);
                              return (
                                <span
                                  key={storeId}
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/60 text-blue-800 dark:text-blue-200 text-xs rounded-md"
                                >
                                  {store?.name || `Store #${storeId}`}
                                  <button
                                    type="button"
                                    onClick={() => handleStoreSelection(storeId)}
                                    className="text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                        )}

                        {/* Dropdown trigger */}
                        <button
                          type="button"
                          onClick={() => setIsStoreDropdownOpen(!isStoreDropdownOpen)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white text-left flex items-center justify-between hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
                        >
                          <span className="text-sm text-gray-600 dark:text-gray-300">
                            {formData.store_ids.length === 0
                              ? 'Select stores...'
                              : `${formData.store_ids.length} store${formData.store_ids.length !== 1 ? 's' : ''} selected`}
                          </span>
                          <svg
                            className={`w-4 h-4 text-gray-400 transition-transform ${isStoreDropdownOpen ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        {/* Dropdown panel */}
                        {isStoreDropdownOpen && (
                          <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-64 flex flex-col">
                            {/* Search input */}
                            <div className="p-2 border-b border-gray-200 dark:border-gray-600">
                              <div className="relative">
                                <svg className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <input
                                  type="text"
                                  value={storeSearchQuery}
                                  onChange={(e) => setStoreSearchQuery(e.target.value)}
                                  placeholder="Search stores..."
                                  autoFocus
                                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                            </div>

                            {/* Store list */}
                            <div className="overflow-y-auto flex-1">
                              {filtersData?.stores
                                ?.filter((store: any) =>
                                  store.name.toLowerCase().includes(storeSearchQuery.toLowerCase())
                                )
                                .map((store: any) => (
                                  <label
                                    key={store.id}
                                    className="flex items-center p-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={formData.store_ids.includes(store.id)}
                                      onChange={() => handleStoreSelection(store.id)}
                                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{store.name}</span>
                                  </label>
                                ))}
                              {filtersData?.stores?.filter((store: any) =>
                                store.name.toLowerCase().includes(storeSearchQuery.toLowerCase())
                              ).length === 0 && (
                                <div className="p-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                                  No stores found
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        <strong className="text-yellow-600 dark:text-yellow-400">
                          Selected: {formData.store_ids.length} store{formData.store_ids.length !== 1 ? 's' : ''}
                        </strong>
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                          (Select All Excludes: {storeSelectAllExclusions.join(', ')})
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Requested By
                      </label>
                      <select
                        value={formData.requested_by_id || ''}
                        onChange={(e) => setFormData({ ...formData, requested_by_id: e.target.value ? Number(e.target.value) : null })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">-- Requested By --</option>
                        {filtersData?.users?.map((user: any) => (
                          <option key={user.id} value={user.id}>{user.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                        Will require an Internal Jobcard? <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.required_jobcard === 0 ? 0 : (formData.required_jobcard || '')}
                        onChange={(e) => setFormData({ ...formData, required_jobcard: e.target.value !== '' ? Number(e.target.value) : 0 })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">-- Select --</option>
                        <option value={1}>Yes</option>
                        <option value={0}>No</option>
                      </select>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        If Yes, an Internal Jobcard will be created automatically when Order Number is generated. Assigned to Technician (for internal orders) and Supplier (for external orders).
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quotation Info */}
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600">
                  <h4 className="font-semibold text-gray-800 dark:text-white mb-4">Quotation Info</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Quotation Date
                      </label>
                      <input
                        type="date"
                        value={formData.quotation_date}
                        onChange={(e) => setFormData({ ...formData, quotation_date: e.target.value })}
                        disabled={isQuoteLocked}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Quotation No.
                      </label>
                      <input
                        type="text"
                        value={formData.quotation_number}
                        onChange={(e) => setFormData({ ...formData, quotation_number: e.target.value })}
                        disabled={isQuoteLocked}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                        placeholder="Quotation No."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Repair Cost / Rand Value
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.repair_cost}
                        onChange={(e) => setFormData({ ...formData, repair_cost: e.target.value })}
                        disabled={isQuoteLocked}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        VAT
                      </label>
                      <select
                        value={formData.vat === 0 ? 0 : (formData.vat || '')}
                        onChange={(e) => setFormData({ ...formData, vat: e.target.value !== '' ? Number(e.target.value) : 0 })}
                        disabled={isQuoteLocked}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                      >
                        <option value="">-- Select --</option>
                        <option value={1}>Included</option>
                        <option value={0}>Excluded</option>
                      </select>
                    </div>
                  </div>

                  {/* Unsaved Quotation Warning + Save Quotation Button */}
                  {!isCreateMode && poId && (
                    <>
                      {!quotationSaved && (
                        <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-md border border-yellow-200 dark:border-yellow-700">
                          <p className="text-sm text-yellow-800 dark:text-yellow-300 flex items-center gap-2">
                            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            You have unsaved quotation details. Click "Save Quotation" to persist changes.
                          </p>
                        </div>
                      )}
                      <div className="mt-3 flex justify-end">
                        <button
                          type="button"
                          onClick={handleSaveQuotation}
                          disabled={actionLoading || isQuoteLocked}
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm"
                        >
                          {actionLoading ? 'Saving...' : 'Save Quotation'}
                        </button>
                      </div>
                    </>
                  )}

                  {po?.quotation && !isQuoteLocked && (
                    <div className="mt-4 flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={handleRemoveQuote}
                        className="px-3 py-1.5 bg-red-500 text-white rounded text-sm hover:bg-red-600 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  {po?.quotation && isQuoteLocked && (
                    <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-700">
                      <p className="text-xs text-blue-700 dark:text-blue-300 flex items-center gap-1">
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Quotation is locked (approved order with order number).
                      </p>
                    </div>
                  )}
                  {po?.quotation && (
                    <div className="mt-4">
                      <button
                        type="button"
                        className="flex items-center w-full justify-between text-left text-green-700 dark:text-green-200 font-semibold focus:outline-none p-2 border border-green-200 dark:border-green-700 rounded-md bg-green-50 dark:bg-green-900/70"
                        onClick={() => setQuoteCollapsed((prev) => !prev)}
                      >
                        <span>View Quotation Attachment</span>
                        <svg
                          className={`w-5 h-5 ml-2 transition-transform ${quoteCollapsed ? 'rotate-90' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                      {!quoteCollapsed && (
                        <div className="mt-2">
                          <iframe
                            src={`${process.env.REACT_APP_BACKEND_URL || ''}/assets/Quotes/${(po.quotation as any).attachment}`}
                            className="w-full h-96 border border-gray-200 dark:border-gray-700 rounded-md"
                            title="Quote Document"
                            onError={(e) => {
                              (e.target as HTMLIFrameElement).style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleNextTab}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Next &rarr;
                </button>
              </div>
            </div>
          )}

          {activeTab === 'supplier' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Supplier / Technician</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Order Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.internal || ''}
                    onChange={(e) => setFormData({ ...formData, internal: e.target.value ? Number(e.target.value) : 0, supplier_id: null, technician_id: null, department_id: null })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">-- Internal/External --</option>
                    <option value={1}>Internal</option>
                    <option value={2}>External</option>
                  </select>
                </div>

                {formData.internal === 1 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Department <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.department_id || ''}
                        onChange={(e) => setFormData({ ...formData, department_id: e.target.value ? Number(e.target.value) : null })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">-- Select department --</option>
                        {filtersData?.stores?.filter((s: any) => s.department_id).map((dept: any) => (
                          <option key={dept.department_id} value={dept.department_id}>{dept.department_name || `Department ${dept.department_id}`}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Technician
                      </label>
                      <select
                        value={formData.technician_id || ''}
                        onChange={(e) => setFormData({ ...formData, technician_id: e.target.value ? Number(e.target.value) : null })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">-- Select Technician --</option>
                        {filtersData?.technicians?.map((tech: any) => (
                          <option key={tech.id} value={tech.id}>{tech.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {formData.internal === 2 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Supplier <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.supplier_id || ''}
                      onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value ? Number(e.target.value) : null })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">-- Select Supplier --</option>
                      {filtersData?.suppliers?.map((supplier: any) => (
                        <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                      ))}
                    </select>
                    {formData.supplier_id && (
                      <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-700">
                        <p className="text-sm text-blue-800 dark:text-blue-300">
                          <strong>Supplier Information:</strong>
                        </p>
                        <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                          Edit supplier details in admin panel
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {formData.internal === 0 && (
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-md border border-yellow-200 dark:border-yellow-700">
                    <p className="text-sm text-yellow-800 dark:text-yellow-300">
                      <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Please select whether this is an Internal or External order to continue.
                    </p>
                  </div>
                )}
              </div>
              <div className="flex justify-center gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handlePreviousTab}
                  className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500 transition-colors"
                >
                  &larr; Previous
                </button>
                <button
                  onClick={handleNextTab}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Next &rarr;
                </button>
              </div>
            </div>
          )}

          {activeTab === 'items' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Items / Assets / Equipment</h3>
              
              {/* Mode Indicator */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-700 mb-4">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  <strong>Current Mode:</strong> {formData.is_order === 1 ? 'Items To Order (GRN)' : 'Items To Purchase'}
                </p>
              </div>

              {/* Category/Subcategory/Type Selection */}
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.category_id || ''}
                      onChange={(e) => setFormData({ ...formData, category_id: e.target.value ? Number(e.target.value) : null, subcategory_id: null })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">-- Select Category --</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>

                  {formData.category_id && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Sub-Category
                      </label>
                      <select
                        value={formData.subcategory_id || ''}
                        onChange={(e) => setFormData({ ...formData, subcategory_id: e.target.value ? Number(e.target.value) : null, is_repair: null })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">-- N/A --</option>
                        {subcategories.map((sub) => (
                          <option key={sub.id} value={sub.id}>{sub.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {formData.category_id && formData.subcategory_id && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.is_repair === null ? '' : formData.is_repair}
                        onChange={(e) => setFormData({ ...formData, is_repair: e.target.value ? Number(e.target.value) : null })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">-- Select Type --</option>
                        <option value={1}>Repair</option>
                        <option value={0}>New Purchase</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* GRN Toggle Switch */}
              <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-md border-2 border-blue-500 dark:border-blue-400 mb-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_order"
                    checked={formData.is_order === 1}
                    onChange={(e) => setFormData({ ...formData, is_order: e.target.checked ? 1 : 0 })}
                    className="w-16 h-8 rounded bg-gray-300 dark:bg-gray-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <label htmlFor="is_order" className="ml-4 text-lg font-medium text-gray-800 dark:text-white cursor-pointer">
                    Create GRN (Goods Received Note)
                  </label>
                </div>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-2 ml-1">
                  <strong>Toggle ON:</strong> Enable GRN creation and tracking for received items.
                  <strong> Toggle OFF:</strong> Skip GRN creation.
                </p>
              </div>

              {/* GRN Warning Banner */}
              {formData.is_order === 1 && (
                <div className="p-4 bg-yellow-100 dark:bg-yellow-900/30 rounded-md border-2 border-yellow-500 dark:border-yellow-400 mb-6">
                  <div className="flex items-start">
                    <svg className="w-8 h-8 text-yellow-600 dark:text-yellow-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <h5 className="font-bold text-yellow-800 dark:text-yellow-300 mb-1">⚠️ THIS IS A GRN ORDER</h5>
                      <p className="text-sm text-yellow-700 dark:text-yellow-400">A Goods Received Note (GRN) will be required for this order to track received items.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Pull from ATR Button */}
              {po?.requisition && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-700 mb-6 flex justify-between items-center">
                  <div>
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      <strong>Linked to ATR:</strong> This order is linked to ATR #{po.requisition.id}.
                      Click the button to refresh assets, descriptions, and equipment from the ATR.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handlePullFromAtr}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Pull from ATR
                  </button>
                </div>
              )}

              <hr className="my-6 border-gray-200 dark:border-gray-700" />

              {/* Equipment Selection (conditional display) */}
              {formData.category_id === 10 && formData.is_repair === 1 && (
                <div className="p-6 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600 text-center mb-6">
                  <p className="text-gray-600 dark:text-gray-400 mb-2">Equipment selection not yet implemented</p>
                  <p className="text-sm text-gray-500 dark:text-gray-500">This will allow selecting equipment from stores for repair orders</p>
                </div>
              )}

              {/* Manual Asset/Item Entry */}
              <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-md border border-gray-300 dark:border-gray-600 mb-6">
                <div className="bg-gray-600 dark:bg-gray-800 text-white px-4 py-2 rounded-t-md -m-4 mb-4">
                  <h4 className="font-semibold">
                    {formData.is_repair === 1 ? (
                      <span className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Manual Asset Entry (Repairs)
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        Manual Item Entry (Purchases)
                      </span>
                    )}
                  </h4>
                </div>
                
                {manualItems.map((item, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4 pb-4 border-b border-gray-300 dark:border-gray-600 last:border-0">
                    <div className="md:col-span-5">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        <strong>{formData.is_repair === 1 ? `Asset To Repair #${index + 1}` : `Item To Purchase #${index + 1}`}</strong>
                      </label>
                      <textarea
                        value={item.asset}
                        onChange={(e) => updateManualItem(index, 'asset', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                        placeholder={formData.is_repair === 1 ? 'Enter asset identifier...' : 'Enter item description...'}
                      />
                    </div>
                    <div className="md:col-span-5">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        <strong>{formData.is_repair === 1 ? `Description of Repair #${index + 1}` : `Description #${index + 1}`}</strong>
                      </label>
                      <textarea
                        value={item.description}
                        onChange={(e) => updateManualItem(index, 'description', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter detailed description..."
                      />
                    </div>
                    <div className="md:col-span-2 flex items-end">
                      <button
                        type="button"
                        onClick={() => removeManualItem(index)}
                        className="w-full px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                        title="Remove this item"
                      >
                        <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Remove
                      </button>
                    </div>
                  </div>
                ))}

                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={addManualItem}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {formData.is_repair === 1 ? 'Add New Asset & Description' : 'Add New Item & Description'}
                  </button>
                </div>
              </div>

              {/* Excel Import */}
              <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-md border border-green-500 dark:border-green-400">
                <button
                  type="button"
                  onClick={() => setExcelImportCollapsed(!excelImportCollapsed)}
                  className="w-full bg-green-600 dark:bg-green-700 text-white px-4 py-2 rounded-t-md flex justify-between items-center"
                >
                  <span className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Order Line Items
                  </span>
                  <svg className={`w-4 h-4 transition-transform ${excelImportCollapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {!excelImportCollapsed && (
                  <div className="p-4 bg-white dark:bg-gray-800">
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-700 mb-4">
                      <p className="text-sm text-blue-800 dark:text-blue-300">
                        <strong>Note:</strong> Import detailed line items from Excel or add them manually below. GRN tracking is controlled by the "Create GRN" toggle above.
                      </p>
                    </div>

                    {/* Excel Import */}
                    <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600 mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm text-gray-600 dark:text-gray-400">Import line items from an Excel file</p>
                        <button
                          type="button"
                          onClick={handleDownloadTemplate}
                          className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                        >
                          Download Template
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="file"
                          accept=".xlsx,.xls,.csv"
                          onChange={(e) => setExcelFile(e.target.files?.[0] || null)}
                          className="flex-1 text-sm text-gray-700 dark:text-gray-300"
                        />
                        <button
                          type="button"
                          onClick={handleExcelImport}
                          disabled={!excelFile || excelImporting}
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm"
                        >
                          {excelImporting ? 'Importing...' : 'Import'}
                        </button>
                      </div>
                    </div>

                    {/* Editable PO Items Table Placeholder */}
                    {po?.items && po.items.length > 0 && (
                      <div className="mb-4">
                        <div className="flex justify-end mb-2">
                          <button
                            type="button"
                            onClick={handleClearAllItems}
                            className="px-3 py-1.5 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors text-sm"
                          >
                            <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Clear All Items
                          </button>
                        </div>
                        <div className="p-6 bg-yellow-50 dark:bg-yellow-900/20 rounded-md border border-yellow-300 dark:border-yellow-600 text-center">
                          <p className="text-gray-600 dark:text-gray-400 mb-2">PO items table not yet implemented</p>
                          <p className="text-sm text-gray-500 dark:text-gray-500">This will display and allow editing of existing PO items</p>
                        </div>
                      </div>
                    )}

                    {/* Add New PO Item Form Placeholder */}
                    <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-md border border-blue-500 dark:border-blue-400">
                      <div className="bg-blue-600 dark:bg-blue-700 text-white px-4 py-2 rounded-t-md -m-4 mb-4">
                        <h4 className="font-semibold text-sm">
                          <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add Item To Order
                        </h4>
                      </div>
                      <div className="p-6 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-600 text-center">
                        <p className="text-gray-600 dark:text-gray-400 mb-2">Add item form not yet implemented</p>
                        <p className="text-sm text-gray-500 dark:text-gray-500">This will allow adding individual items to the order</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-center gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handlePreviousTab}
                  className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500 transition-colors"
                >
                  &larr; Previous
                </button>
                <button
                  onClick={handleNextTab}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Next &rarr;
                </button>
              </div>
            </div>
          )}

          {activeTab === 'approval' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Approval & Submission</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Fulfillment Status */}
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600">
                  <h4 className="font-semibold text-gray-800 dark:text-white mb-4">Fulfillment Status</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Status
                      </label>
                      <select
                        value={formData.fulfillment_id || ''}
                        onChange={(e) => setFormData({ ...formData, fulfillment_id: e.target.value ? Number(e.target.value) : null })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">-- Select Status --</option>
                        {filtersData?.fulfillments?.map((status: any) => (
                          <option key={status.id} value={status.id}>{status.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Comment
                      </label>
                      <textarea
                        value={formData.comment}
                        onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Add a comment..."
                      />
                    </div>
                  </div>
                </div>

                {/* Save Changes */}
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600">
                  <h4 className="font-semibold text-gray-800 dark:text-white mb-4">Save Changes</h4>
                  {formData.is_order === 1 && (!po?.items || po.items.length === 0) && (
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-md border border-yellow-200 dark:border-yellow-700 mb-4">
                      <p className="text-sm text-yellow-800 dark:text-yellow-300">
                        <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <strong>Cannot Save:</strong> Item Orders must have at least 1 PO item.
                      </p>
                      <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">Please add items using the "Items" tab before saving.</p>
                    </div>
                  )}
                  <div className="text-center">
                    <button
                      onClick={handleSave}
                      disabled={formData.is_order === 1 && (!po?.items || po.items.length === 0)}
                      className="w-full px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                      </svg>
                      {isCreateMode ? 'Create Purchase Order' : 'Save Changes'}
                    </button>
                  </div>
                </div>

                {/* Approvers */}
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600">
                  <h4 className="font-semibold text-gray-800 dark:text-white mb-4">Approvers</h4>
                  <div className="space-y-4">
                    {(po?.quotation || formData.internal === 1) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          First Approver <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.approver_id || ''}
                          onChange={(e) => setFormData({ ...formData, approver_id: e.target.value ? Number(e.target.value) : null })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">-- Select Approver --</option>
                          {filtersData?.approvers?.map((user: any) => (
                            <option key={user.id} value={user.id}>{user.name}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {formData.approver_id && (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="requireSecondApprover"
                          checked={requireSecondApprover}
                          onChange={(e) => setRequireSecondApprover(e.target.checked)}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <label htmlFor="requireSecondApprover" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          Require second approver
                        </label>
                      </div>
                    )}

                    {requireSecondApprover && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Second Approver <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.second_approver_id || ''}
                          onChange={(e) => setFormData({ ...formData, second_approver_id: e.target.value ? Number(e.target.value) : null })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">-- Second Approver --</option>
                          {filtersData?.approvers?.filter((u: any) => u.id !== formData.approver_id).map((user: any) => (
                            <option key={user.id} value={user.id}>{user.name}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {formData.is_order === 1 && (!po?.items || po.items.length === 0) && (
                      <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-200 dark:border-red-700">
                        <p className="text-sm text-red-800 dark:text-red-300">
                          <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <strong>Cannot Submit:</strong> Item Orders must have at least 1 PO item.
                        </p>
                        <p className="text-xs text-red-700 dark:text-red-400 mt-1">Please add items using the "Items" tab before submitting.</p>
                      </div>
                    )}

                    <button
                      onClick={handleSubmit}
                      disabled={(formData.is_order === 1 && (!po?.items || po.items.length === 0)) || actionLoading}
                      className="w-full px-6 py-3 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      {actionLoading ? 'Processing...' : 'Submit for approval'}
                    </button>

                    {/* TBA Submission Button */}
                    {!isCreateMode && poId && (
                      <button
                        onClick={() => setShowTbaModal(true)}
                        disabled={actionLoading}
                        className="w-full px-6 py-3 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 transition-colors disabled:opacity-50"
                      >
                        <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                        </svg>
                        Submit for TBA Assessment
                      </button>
                    )}

                    {/* Split Order Button */}
                    {!isCreateMode && poId && po?.stores && po.stores.length > 1 && po.status_id === 1 && (
                      <button
                        onClick={handleSplitOrder}
                        disabled={actionLoading}
                        className="w-full px-6 py-3 bg-amber-500 text-white rounded-md hover:bg-amber-600 transition-colors disabled:opacity-50"
                      >
                        <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                        Split Order (per store)
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex justify-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handlePreviousTab}
                  className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500 transition-colors"
                >
                  &larr; Previous
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* TBA Submission Modal */}
      {showTbaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md p-6 rounded-lg bg-white dark:bg-slate-800 shadow-xl">
            <h3 className="text-lg font-semibold mb-3 text-slate-900 dark:text-white">Submit for TBA Assessment</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
              Select a TBA approver to notify. The order will be sent to the supplier for assessment.
            </p>
            <select
              value={tbaApprover || ''}
              onChange={(e) => setTbaApprover(e.target.value ? Number(e.target.value) : null)}
              className="w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm"
            >
              <option value="">Select TBA Approver...</option>
              {filtersData?.approvers?.map((user: any) => (
                <option key={user.id} value={user.id}>{user.name}</option>
              ))}
            </select>
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleTbaSubmit}
                disabled={!tbaApprover || actionLoading}
                className="flex-1 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : 'Submit for TBA'}
              </button>
              <button
                onClick={() => { setShowTbaModal(false); setTbaApprover(null); }}
                className="px-4 py-2 rounded-lg border bg-white dark:bg-slate-900/50 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderEditPage;
